export type Box = { x1: number; y1: number; x2: number; y2: number };

// Expand a box outward by `fraction` of its own width/height on each side.
//
// A DinoFast detection box is the axis-aligned envelope of the *above-threshold*
// patch component, so it bounds the object but stops at the object's edge — a
// cast shadow is sub-threshold and leaks just *past* that edge, outside the box.
// Growing the box pulls that sub-threshold ring into SAM2's shadow-rejection
// windows (negative point prompts + heatmap-aligned mask selection) without
// changing the tight box prompt SAM2 itself is given. `fraction <= 0` is a
// no-op, preserving the legacy box-clipped behavior.
export function expandBox(box: Box, fraction: number): Box {
  if (fraction <= 0) return box;

  const mx = (box.x2 - box.x1) * fraction;
  const my = (box.y2 - box.y1) * fraction;

  return {
    x1: box.x1 - mx,
    y1: box.y1 - my,
    x2: box.x2 + mx,
    y2: box.y2 + my,
  };
}

// How far past the detection box SAM2's shadow-rejection windows extend —
// negative point prompts and heatmap penalty samples. The box bounds the
// above-threshold object, but a cast shadow is sub-threshold and leaks just
// past that edge, so a box-clipped window never sees it; sampling the
// surrounding ring lets SAM2 (and the multimask selector) reject the
// over-inclusive mask that swallows the shadow. The tight box prompt SAM2 is
// given is unchanged — only the sampling window widens. Shared by runLocate
// and the teach preview loops so prompts stay identical between them.
export const SAM2_SHADOW_MARGIN_FRACTION = 0.2;
